import { atom } from "jotai";
import { theme } from "antd";
import { PRIMARY_COLOR, PRIMARY_ACTIVE_BG } from "../constants";

// ── Persistence ──────────────────────────────────────────────────────
const STORAGE_KEY = "qureal_theme";

export function saveTheme(key) {
    localStorage.setItem(STORAGE_KEY, key);
}

export function restoreTheme() {
    return localStorage.getItem(STORAGE_KEY) || "light";
}

// Layout related overwrites for mobile devices.
// iOS-inspired: 44pt touch targets, generous border radius, hairline borders.
// Never overrides color or font tokens — layout geometry only.
const mobileLayoutTheme = {
    token: {
        // ── Border radius — iOS uses generous, consistent rounding ────────
        // iOS system default: ~10pt for controls, ~14pt for cards/sheets
        borderRadius: 10, // antd default: 6
        borderRadiusSM: 8, // antd default: 4
        borderRadiusLG: 14, // antd default: 8  (used by Modal, Card, Drawer)
        borderRadiusXS: 6, // antd default: 2
        borderRadiusOuter: 6, // antd default: 4  (Tooltip/Popover outer)

        // ── Touch targets — iOS HIG minimum: 44×44pt ─────────────────────
        controlHeight: 44, // antd default: 32
        controlHeightSM: 36, // antd default: 24
        controlHeightLG: 52, // antd default: 40
        controlHeightXS: 28, // antd default: 16

        // ── Control interactive size — checkbox, radio touch area ─────────
        controlInteractiveSize: 22, // antd default: 16

        // ── Spacing — 8pt grid, comfortable thumb reach ───────────────────
        paddingXXS: 4,
        paddingXS: 8,
        paddingSM: 12,
        padding: 16,
        paddingMD: 20,
        paddingLG: 24,
        paddingXL: 32,

        // Content-area padding (used by List, Table, Notification, etc.)
        paddingContentHorizontalLG: 24,
        paddingContentVerticalLG: 16,
        paddingContentHorizontal: 16,
        paddingContentVertical: 12,
        paddingContentHorizontalSM: 16,
        paddingContentVerticalSM: 8,

        marginXXS: 4,
        marginXS: 8,
        marginSM: 12,
        margin: 16,
        marginMD: 20,
        marginLG: 24,
        marginXL: 32,
        marginXXL: 48,

        // ── Borders — iOS uses hairline (0.5pt) separators ────────────────
        lineWidth: 0.5, // antd default: 1
        lineWidthBold: 1, // antd default: 2
        lineWidthFocus: 3, // antd default: 3  (keep for a11y)

        // ── Inline control padding ─────────────────────────────────────────
        controlPaddingHorizontal: 16, // antd default: 12
        controlPaddingHorizontalSM: 12, // antd default: 8
    },
    components: {
        // ── Inputs & Controls ─────────────────────────────────────────────
        Button: {
            borderRadius: 10,
            paddingInline: 20,
            paddingInlineLG: 28,
            paddingInlineSM: 14,
        },
        Input: {
            borderRadius: 10,
            paddingBlock: 11, // centers text in 44px control
            paddingBlockLG: 14,
            paddingBlockSM: 8,
            paddingInline: 16,
            paddingInlineLG: 20,
            paddingInlineSM: 12,
        },
        InputNumber: {
            borderRadius: 10,
        },
        Select: {
            borderRadius: 10,
        },
        DatePicker: {
            borderRadius: 10,
            paddingInline: 16,
            paddingBlock: 11,
            paddingBlockSM: 8,
            paddingInlineSM: 12,
            paddingBlockLG: 14,
            paddingInlineLG: 20,
        },
        // ── Switch — iOS toggle proportions: 51×31pt ──────────────────────
        Switch: {
            trackHeight: 31,
            trackMinWidth: 51,
            handleSize: 27,
            trackPadding: 2,
        },
        // ── Checkbox / Radio ──────────────────────────────────────────────
        Checkbox: {
            controlInteractiveSize: 22,
        },
        Radio: {
            controlInteractiveSize: 22,
        },
        // ── Segmented (iOS pill/segment control) ──────────────────────────
        Segmented: {
            borderRadius: 10,
            trackPadding: 3,
        },
        // ── Navigation & Menus ────────────────────────────────────────────
        // iOS table-view rows: 44–48pt
        Menu: {
            itemHeight: 48,
            itemBorderRadius: 10,
            subMenuItemBorderRadius: 8,
        },
        Tabs: {
            borderRadius: 10,
            cardHeight: 44,
        },
        Pagination: {
            borderRadius: 10,
            itemSize: 40,
            itemSizeSM: 32,
        },
        // ── Overlays & Sheets ─────────────────────────────────────────────
        // iOS sheets use very rounded top corners (20pt+)
        Modal: {
            borderRadiusLG: 20,
        },
        Drawer: {
            borderRadiusLG: 20,
            footerPaddingBlock: 12,
            footerPaddingInline: 20,
        },
        Tooltip: {
            borderRadius: 10,
            borderRadiusOuter: 12,
        },
        Popover: {
            borderRadiusOuter: 14,
        },
        Notification: {
            borderRadiusLG: 16,
        },
        // ── Content Containers ────────────────────────────────────────────
        Card: {
            borderRadiusLG: 14,
            bodyPadding: 20,
            headerPadding: "14px 20px",
        },
        List: {
            itemPadding: "12px 0",
            itemPaddingSM: "8px 16px",
            itemPaddingLG: "16px 24px",
        },
        Table: {
            cellPaddingBlock: 14,
            cellPaddingInline: 16,
            cellPaddingBlockMD: 10,
            cellPaddingInlineMD: 12,
            cellPaddingBlockSM: 6,
            cellPaddingInlineSM: 8,
        },
        Collapse: {
            headerPadding: "14px 20px",
            contentPadding: "12px 20px",
            borderRadiusLG: 12,
        },
        Form: {
            itemMarginBottom: 16,
            verticalLabelPadding: "0 0 6px",
            verticalLabelMargin: 0,
        },
        // ── Slider — slightly thicker rail for touch ──────────────────────
        Slider: {
            railSize: 4,
        },
        Tag: {
            borderRadiusSM: 6,
        },
    },
};

// ── Theme definitions ────────────────────────────────────────────────
const lightTheme = {
    token: {
        colorPrimary: PRIMARY_COLOR,
        colorInfo: "#3FC5EA",
        colorSuccess: "#2F9E5C",
        colorWarning: "#D4A017",
        colorError: "#C64545",

        colorBgBase: "#FCFBF7",
        colorBgLayout: "#f5f5f5",
        colorBgContainer: "#FFFFFF",
        colorBgElevated: "#FFFFFF",
        colorBgSpotlight: "#0F2A2E",

        colorPrimaryBg: "#D2F4EE",

        colorText: "#141413",
        colorTextSecondary: "#3D3C36",
        colorTextTertiary: "#6C6A60",
        colorTextQuaternary: "#8E8B7E",

        colorBorder: "#EDE7D6",
        colorBorderSecondary: "#F3EEE0",

        // borderRadius: 10,
        // borderRadiusLG: 14,
        // borderRadiusXS: 4,
        // borderRadiusSM: 6,

        fontFamily:
            '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        fontFamilyCode:
            '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
        fontFamilyHeading:
            '"Source Serif 4", "Source Serif Pro", Georgia, serif',

        // controlHeight: 44,
        // controlHeightSM: 36,
        // controlHeightLG: 52,
    },
    components: {
        Form: {
            itemMarginBottom: 12,
            verticalLabelPadding: "0 0 6px",
        },
        Menu: {
            itemSelectedBg: "#edeff1ff",
            itemHeight: 44,
            itemBorderRadius: 10,
        },
        Calendar: {
            itemActiveBg: PRIMARY_ACTIVE_BG,
        },
    },
};

const darkTheme = {
    algorithm: theme.darkAlgorithm,
    token: {
        colorPrimary: "#00998E",
        colorInfo: "#3FC5EA",
        colorSuccess: "#2F9E5C",
        colorWarning: "#D4A017",
        colorError: "#C64545",

        colorBgBase: "#0F2A2E",
        colorBgLayout: "#0A1A1C",
        colorBgContainer: "#1A3A3E",
        colorBgElevated: "#234246",
        colorBgSpotlight: "#0F2A2E",

        colorPrimaryBg: "rgba(0, 153, 142, 0.15)",

        colorText: "#E8E4D9",
        colorTextSecondary: "#B5B0A0",
        colorTextTertiary: "#8A8575",
        colorTextQuaternary: "#6A6558",

        colorBorder: "#2D4A4E",
        colorBorderSecondary: "#234246",

        // borderRadius: 10,
        // borderRadiusLG: 14,
        // borderRadiusXS: 4,
        // borderRadiusSM: 6,

        fontFamily:
            '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        fontFamilyCode:
            '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
        fontFamilyHeading:
            '"Source Serif 4", "Source Serif Pro", Georgia, serif',

        // controlHeight: 44,
        // controlHeightSM: 36,
        // controlHeightLG: 52,
    },
    components: {
        Form: {
            itemMarginBottom: 12,
            verticalLabelPadding: "0 0 6px",
        },
        Menu: {
            itemSelectedBg: "#234246",
            itemHeight: 44,
            itemBorderRadius: 10,
        },
        Calendar: {
            itemActiveBg: "rgba(0, 153, 142, 0.15)",
        },
        Card: {
            colorBgContainer: "#1A3A3E",
        },
    },
};

const oceanTheme = {
    token: {
        colorPrimary: "#023E8A",
        colorInfo: "#3FC5EA",
        colorSuccess: "#2F9E5C",
        colorWarning: "#D4A017",
        colorError: "#C64545",

        colorBgBase: "#FCFBF7",
        colorBgLayout: "#f5f5f5",
        colorBgContainer: "#FFFFFF",
        colorBgElevated: "#FFFFFF",
        colorBgSpotlight: "#0B1F3A",

        colorPrimaryBg: "#E0EEFF",

        colorText: "#141413",
        colorTextSecondary: "#3D3C36",
        colorTextTertiary: "#6C6A60",
        colorTextQuaternary: "#8E8B7E",

        colorBorder: "#EDE7D6",
        colorBorderSecondary: "#F3EEE0",

        // borderRadius: 10,
        // borderRadiusLG: 14,
        // borderRadiusXS: 4,
        // borderRadiusSM: 6,

        fontFamily:
            '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        fontFamilyCode:
            '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
        fontFamilyHeading:
            '"Source Serif 4", "Source Serif Pro", Georgia, serif',

        // controlHeight: 44,
        // controlHeightSM: 36,
        // controlHeightLG: 52,
    },
    components: {
        Form: {
            itemMarginBottom: 12,
            verticalLabelPadding: "0 0 6px",
        },
        Menu: {
            itemSelectedBg: "#E0EEFF",
            itemHeight: 44,
            itemBorderRadius: 10,
        },
        Calendar: {
            itemActiveBg: "rgba(2, 62, 138, 0.12)",
        },
        Button: {
            primaryShadow: "none",
        },
    },
};

const defaultTheme = {
    token: {
        colorPrimary: "#00998E",
        colorPrimaryHover: "#007A71",
        colorPrimaryActive: "#007A71",
        colorLink: "#00998E",
        colorLinkHover: "#007A71",

        colorBgBase: "#FAF8F2",
        colorBgContainer: "#FFFFFF",
        colorBgElevated: "#FFFFFF",
        colorBgLayout: "#FAF8F2",

        colorText: "#1B2B28",
        colorTextSecondary: "#5C6B66",
        colorTextTertiary: "#5C6B66",
        colorTextPlaceholder: "#5C6B66",
        colorTextHeading: "#1B2B28",

        colorBorder: "#E6E1D0",
        colorBorderSecondary: "#E6E1D0",
        colorSplit: "#E6E1D0",

        colorSuccess: "#00998E",
        colorInfo: "#00998E",
        colorWarning: "#D9C179",
        colorError: "#C64545",

        colorPrimaryBg: "#E1F0EE",

        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        fontFamilyHeading: '"Fraunces", "Newsreader", Georgia, serif',
        fontFamilyCode: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
        fontSize: 16,
        lineHeight: 1.65,

        borderRadius: 8,
        borderRadiusLG: 12,
        borderRadiusSM: 6,
        borderRadiusXS: 4,

        boxShadow: "0 2px 16px rgba(27,43,40,0.07)",
        boxShadowSecondary: "0 1px 6px rgba(27,43,40,0.05)",

        motionDurationSlow: "300ms",
        motionDurationMid: "200ms",
        motionDurationFast: "150ms",
        motionEaseInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    },
    components: {
        Button: {
            borderRadius: 999,
            borderRadiusLG: 999,
            borderRadiusSM: 999,
            primaryColor: "#FFFFFF",
            fontWeight: 500,
        },
        Input: {
            borderRadius: 8,
            borderRadiusLG: 10,
            colorBgContainer: "#FFFFFF",
            activeBorderColor: "#00998E",
            hoverBorderColor: "#00998E",
        },
        Select: {
            borderRadius: 8,
            colorBgContainer: "#FFFFFF",
        },
        Card: {
            colorBgContainer: "#FFFFFF",
            borderRadius: 12,
            boxShadow: "0 2px 16px rgba(27,43,40,0.07)",
        },
        Modal: {
            borderRadius: 16,
            colorBgElevated: "#FFFFFF",
        },
        Drawer: {
            colorBgElevated: "#FFFFFF",
        },
        Table: {
            colorBgContainer: "#FFFFFF",
            headerBg: "#FDFCF8",
            borderColor: "#E6E1D0",
            rowHoverBg: "#F7F4E8",
        },
        Tabs: {
            inkBarColor: "#00998E",
            itemActiveColor: "#00998E",
            itemHoverColor: "#00998E",
            itemSelectedColor: "#00998E",
        },
        Tag: {
            borderRadius: 999,
        },
        Badge: {
            colorPrimary: "#00998E",
        },
        Menu: {
            colorBgContainer: "#FFFFFF",
            itemHoverBg: "#E1F0EE",
            itemSelectedBg: "#E1F0EE",
            itemSelectedColor: "#00998E",
        },
        Tooltip: {
            colorBgDefault: "#1B2B28",
        },
        Progress: {
            defaultColor: "#00998E",
        },
        Switch: {
            colorPrimary: "#00998E",
        },
        Checkbox: {
            colorPrimary: "#00998E",
        },
        Radio: {
            colorPrimary: "#00998E",
        },
        Slider: {
            colorPrimary: "#00998E",
        },
        Form: {
            labelColor: "#5C6B66",
            labelFontSize: 14,
            itemMarginBottom: 12,
            verticalLabelPadding: "0 0 6px",
        },
        Alert: {
            colorInfoBg: "#E1F0EE",
            colorInfoBorder: "#00998E",
            colorInfoText: "#1B2B28",
        },
        Calendar: {
            itemActiveBg: "#E1F0EE",
        },
    },
};

// ── Theme registry ───────────────────────────────────────────────────
export const themes = {
    light: lightTheme,
    dark: darkTheme,
    ocean: oceanTheme,
    default: defaultTheme,
};


// ── Mobile theme merge ───────────────────────────────────────────────
// Deep-merges mobileLayoutTheme on top of a base theme config.
// Tokens from mobileLayoutTheme win; colors and fonts are never
// touched since mobileLayoutTheme.token contains no such keys.
function mergeWithMobile(base) {
    const mergedComponents = {};
    const allKeys = new Set([
        ...Object.keys(base.components || {}),
        ...Object.keys(mobileLayoutTheme.components),
    ]);
    for (const k of allKeys) {
        mergedComponents[k] = {
            ...(base.components?.[k] || {}),
            ...(mobileLayoutTheme.components[k] || {}),
        };
    }
    return {
        ...base,
        token: { ...base.token, ...mobileLayoutTheme.token },
        components: mergedComponents,
    };
}

// Evaluated once at module load — real mobile devices never resize.
// For desktop dev, use browser DevTools mobile emulation (requires page reload).
const isMobileViewport =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches;

// ── Atoms ────────────────────────────────────────────────────────────
export const themeAtom = atom(restoreTheme());

export const themeConfigAtom = atom((get) => {
    const key = get(themeAtom);
    const base = themes[key] || themes.default;
    return isMobileViewport ? mergeWithMobile(base) : base;
});
